import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, streamChat } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import PersonaCard from "@/components/PersonaCard";
import VoiceOrb from "@/components/VoiceOrb";
import OnboardingCelebration from "@/components/OnboardingCelebration";
import MessageBubble from "@/components/shared/MessageBubble";
import ArtifactActions from "@/components/shared/ArtifactActions";
import AudioFirstOverlay, { VoiceHelpAction } from "@/components/shared/AudioFirstOverlay";
import ChatComposer from "@/components/shared/ChatComposer";
import useVoiceSession from "@/hooks/useVoiceSession";
import useThreadAttach from "@/hooks/useThreadAttach";
import { docsFromMessages, messageFromAttach, promptToUseDoc } from "@/lib/attach";
import useStickyScroll from "@/hooks/useStickyScroll";
import { shouldCloseFullscreen } from "@/lib/voiceStage";
import { friendlyError } from "@/lib/errors";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { ArrowRight, ArrowDown, SlidersHorizontal, Keyboard, Mic } from "lucide-react";
import { toast } from "sonner";

const PERSONA_FIELDS = [
  "bandeira", "proposito", "personalidade", "tom_de_voz",
  "girias_expressoes", "posicionamento", "diferencial", "emocao_gerada",
];

export default function OnboardingPersona() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [persona, setPersona] = useState({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  const [savingArtifactId, setSavingArtifactId] = useState(null);
  const [orbState, setOrbState] = useState("idle");
  const [showIntro, setShowIntro] = useState(true);
  const [historyReady, setHistoryReady] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [transitionError, setTransitionError] = useState(false);
  const [transitionRetry, setTransitionRetry] = useState(null);
  const [personaSheet, setPersonaSheet] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const feed = useStickyScroll(messages);
  const initRef = useRef(false);
  const finalizeRef = useRef(false);
  // Mensagem digitada enquanto a IA responde: sai sozinha ao fim do stream.
  const queuedRef = useRef(null);
  const abortRef = useRef(null);
  const finalizingRef = useRef(false);
  const threadRef = useRef(null);
  const inputRef = useRef(null);

  const refreshPersona = async () => {
    try {
      const { data } = await api.get("/persona/me");
      setPersona(data || {});
    } catch {}
  };

  const triggerFinalize = () => {
    if (!finalizeRef.current || finalizingRef.current) return;
    finalizingRef.current = true;
    // Give the last audio/text a beat to drain, then close voice + celebrate.
    setTimeout(() => {
      if (voice.active) voice.stop();
      setCelebrate(true);
    }, 500);
  };

  const refreshConfirmedPersona = async (retry) => {
    setTransitionError(false);
    setTransitionRetry(null);
    try {
      const updatedUser = await refresh();
      const avatarUnlocked = updatedUser?.persona_confirmada === true &&
        ["brand_persona_pronta", "concluido"].includes(updatedUser?.status_onboarding);
      if (!avatarUnlocked) throw new Error("persona_not_confirmed");
      return true;
    } catch {
      setTransitionError(true);
      setTransitionRetry(retry);
      toast.error("Não foi possível confirmar sua Brand Persona agora. Tente novamente.");
      return false;
    }
  };

  const advanceToAvatar = async () => {
    if (await refreshConfirmedPersona("advance")) nav("/onboarding/avatar");
  };

  const finishCelebration = async () => {
    setCelebrate(false);
    if (!(await refreshConfirmedPersona("celebration"))) return;
    let hasAvatar = false;
    try {
      const { data } = await api.get("/avatars", { params: { status: "complete" } });
      hasAvatar = (data || []).length > 0;
    } catch {
      setTransitionError(true);
      setTransitionRetry("celebration");
      toast.error("Não foi possível verificar seu Avatar agora. Tente novamente.");
      return;
    }
    nav(hasAvatar ? "/dashboard" : "/onboarding/avatar", { replace: true });
  };

  const handleVoiceEvent = (evt) => {
    if (evt.type === "transcript") {
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === evt.role && last.live) {
          next[next.length - 1] = { ...last, content: last.content + evt.text };
        } else {
          next.push({ id: `v-${Date.now()}-${Math.random()}`, role: evt.role, content: evt.text, live: true });
        }
        return next;
      });
    } else if (evt.type === "turn_complete") {
      setMessages((m) => m.map((x) => (x.live ? { ...x, live: false } : x)));
      refreshPersona();
      refresh();
      if (finalizeRef.current) triggerFinalize();
    } else if (evt.type === "tool_result") {
      // update_persona refreshes the card in place — it must NOT close the stage.
      if (evt.name === "update_persona" && evt.result?.ok) refreshPersona();
      // finalize_onboarding is the only tool that ends the stage here; it closes
      // with a drain (triggerFinalize) once the closing turn completes.
      if (shouldCloseFullscreen(evt).mode === "drain") finalizeRef.current = true;
    } else if (evt.type === "error") {
      toast.error(evt.message || "A sessão de voz falhou. Tente novamente.");
    }
  };

  const voice = useVoiceSession({
    agent: "brand-persona",
    threadId: thread?.id,
    onEvent: handleVoiceEvent,
  });

  const startVoice = () => {
    if (!thread?.id || !historyReady || voice.active || sending) return;
    setShowIntro(false);
    voice.dismissRecovery?.();
    voice.start();
  };
  const endVoice = () => { voice.stop(); };
  const continueVoiceByText = () => {
    voice.continueWithText?.();
    setShowIntro(false);
  };

  const attach = useThreadAttach({
    threadId: thread?.id,
    onAttached: (data) => {
      setMessages((m) => [...m, messageFromAttach(data)]);
    },
  });

  // Chegada após reset da Brand Persona (Minha conta / admin): avisa uma vez e
  // limpa o parâmetro para o aviso não repetir em recargas.
  useEffect(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    if (!search || !new URLSearchParams(search).has("reset")) return;
    toast.success("Sua Brand Persona foi apagada. Vamos recomeçar do zero ✦");
    try { window.history.replaceState(null, "", window.location.pathname); } catch { /* ignore */ }
  }, []);

  // Initialize (or recover) the existing conversation. A retry reuses the
  // thread already resolved during a partial load, so it never creates a new
  // empty Brand Persona thread after a transient history failure.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    setHistoryError(false);
    (async () => {
      try {
        const personaRes = await api.get("/persona/me");
        setPersona(personaRes.data || {});
        let t = threadRef.current;
        if (!t) {
          const ts = await api.get("/threads", { params: { agent_slug: "brand-persona" } });
          t = ts.data[0];
          if (!t) {
            const created = await api.post("/threads", { agent_slug: "brand-persona" });
            t = created.data;
          }
          threadRef.current = t;
        }
        setThread(t);
        const ms = await api.get(`/threads/${t.id}/messages`);
        if (ms.data.length === 0) {
          const agentInfo = await api.get(`/agents/brand-persona`);
          setMessages([{ id: "greet", role: "assistant", content: agentInfo.data.greeting }]);
        } else {
          setMessages(ms.data);
          setShowIntro(false);
        }
        setHistoryReady(true);
      } catch (e) {
        setHistoryError(true);
        toast.error(friendlyError(e));
      }
    })();
  }, [loadAttempt]);

  const stopStreaming = () => {
    abortRef.current?.abort();
  };

  const attachDraftToLastBubble = (artifactId) => {
    setMessages((m) => {
      const next = [...m];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === "assistant") {
          next[i] = { ...next[i], draftArtifactId: artifactId, artifactDecision: null };
          break;
        }
      }
      return next;
    });
  };

  const saveDraftArtifact = async (messageId, artifactId) => {
    setSavingArtifactId(artifactId);
    try {
      await api.post(`/artifacts/${artifactId}/save`);
      setMessages((m) =>
        m.map((x) => (x.id === messageId ? { ...x, artifactDecision: "saved" } : x))
      );
      toast.success("Salvo na Biblioteca ✦");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSavingArtifactId(null);
    }
  };

  const detachDoc = async (doc) => {
    if (!thread?.id || !doc?.id) return;
    try {
      await api.delete(`/threads/${thread.id}/attach/${doc.id}`);
      setMessages((m) => m.filter((x) => x.id !== doc.id));
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  const sendMessage = async (contentArg) => {
    const content = (contentArg ?? input).trim();
    if (!content || !thread) return;
    // Streaming em curso: enfileira em vez de descartar em silêncio.
    if (sending) {
      queuedRef.current = content;
      setQueued(true);
      if (contentArg == null) setInput("");
      return;
    }
    setInput("");
    setShowIntro(false);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
    setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: "" }]);
    setSending(true);
    setOrbState("thinking");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat(thread.id, content, async (evt) => {
        if (evt.type === "token") {
          setOrbState("speaking");
          setMessages((m) => {
            const next = [...m];
            const lastIdx = next.length - 1;
            if (next[lastIdx]?.role === "assistant" && !next[lastIdx].closed) {
              next[lastIdx] = { ...next[lastIdx], content: next[lastIdx].content + evt.content };
              return next;
            }
            return [...next, { id: `a-${Date.now()}-${next.length}`, role: "assistant", content: evt.content }];
          });
        } else if (evt.type === "message_end") {
          setMessages((m) => {
            const next = [...m];
            const lastIdx = next.length - 1;
            if (next[lastIdx]?.role === "assistant") next[lastIdx] = { ...next[lastIdx], closed: true };
            return next;
          });
        } else if (evt.type === "tool_call") {
          setOrbState("tool");
        } else if (evt.type === "tool_result") {
          if (evt.name === "update_persona" && evt.result?.ok) {
            const { data } = await api.get("/persona/me");
            setPersona(data || {});
          }
          if (evt.name === "finalize_onboarding" && evt.result?.ok) finalizeRef.current = true;
        } else if (evt.type === "artifact") {
          // O Manual de Brand Persona vinha sendo criado e ignorado por esta tela.
          if (evt.artifact.status === "draft") attachDraftToLastBubble(evt.artifact.id);
        } else if (evt.type === "error") {
          toast.error(evt.message || "A ValiosIA teve um problema ao responder. Tente de novo.");
          setOrbState("idle");
          setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
        } else if (evt.type === "done") {
          setOrbState("idle");
          if (finalizeRef.current) triggerFinalize();
        }
      }, controller.signal);
      await refresh();
    } catch (e) {
      setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
      if (e?.name === "AbortError") {
        setOrbState("idle");
      } else {
        toast.error(friendlyError(e));
        setOrbState("error");
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      const pending = queuedRef.current;
      if (pending) {
        queuedRef.current = null;
        setQueued(false);
        setTimeout(() => sendMessage(pending), 0);
      }
    }
  };

  const personaFilled = PERSONA_FIELDS.filter((f) => persona[f]).length;
  const canAdvance = () => persona.persona_confirmada === true;
  const introVisible = showIntro && !voice.recovery && historyReady && thread && messages.length <= 1 && !voice.active;
  const hasConversation = messages.some((m) => m.role === "user");
  const voiceActionLabel = voice.status === "error"
    ? "Tentar voz novamente"
    : hasConversation ? "Retomar conversa por voz" : "Iniciar conversa por voz";
  const orbActive = voice.active ? voice.status : orbState;

  return (
    <div className="h-dvh bg-brand-bg flex flex-col overflow-hidden" data-testid="onboarding-persona-page">
      <AppHeader />
      <div className="flex-1 min-h-0 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 pt-3 lg:pt-5 pb-3 grid grid-rows-1 lg:grid-cols-[1.3fr_0.9fr] gap-4 lg:gap-8">
        {/* Conversation pane */}
        <div className="relative bg-brand-surface border border-brand-line rounded-3xl flex flex-col min-h-0 overflow-hidden">
          <div className="flex items-center gap-3 lg:gap-4 px-4 lg:px-7 py-3.5 lg:py-4 border-b border-brand-line">
            <VoiceOrb
              state={orbActive}
              size={48}
              levels={voice.active ? voice.levels : null}
              showLabel={false}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.24em] uppercase text-brand-rose font-ui">Etapa 1 de 2</div>
              <div className="font-display text-lg text-brand-ink truncate">Sua Brand Persona</div>
            </div>
            {/* Mobile: open persona summary */}
            <button
              type="button"
              data-testid="persona-summary-toggle"
              onClick={() => setPersonaSheet(true)}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink2 hover:text-brand-rose transition-colors min-h-[44px]"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {personaFilled}/{PERSONA_FIELDS.length}
            </button>
            {canAdvance() && (
              <button
                data-testid="advance-to-avatar"
                onClick={advanceToAvatar}
                className="inline-flex items-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-4 lg:px-5 py-2.5 rounded-full text-[13px] font-ui transition-colors min-h-[44px]"
              >
                <span className="hidden sm:inline">Avançar para o avatar</span>
                <span className="sm:hidden">Avançar</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <div
            ref={feed.ref}
            onScroll={feed.onScroll}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 lg:px-7 py-5 lg:py-6 space-y-4 scrollbar-soft"
            data-testid="persona-chat-feed"
          >
            {messages.map((m) => (
              <div key={m.id}>
                <MessageBubble message={m} />
                {/* O Manual de Brand Persona também nasce como rascunho: sem as
                    ações aqui ele nunca chegava à Biblioteca (UAT 2026-08-13). */}
                {m.draftArtifactId && (
                  <ArtifactActions
                    saving={savingArtifactId === m.draftArtifactId}
                    done={m.artifactDecision || null}
                    onSave={() => saveDraftArtifact(m.id, m.draftArtifactId)}
                    onKeepDraft={() =>
                      setMessages((prev) =>
                        prev.map((x) =>
                          x.id === m.id ? { ...x, artifactDecision: "draft" } : x
                        )
                      )
                    }
                  />
                )}
              </div>
            ))}
          </div>

          {!feed.atBottom && (
            <button
              type="button"
              data-testid="scroll-to-latest"
              onClick={() => feed.scrollToBottom()}
              className="absolute left-1/2 -translate-x-1/2 bottom-24 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink shadow-lg hover:border-brand-rose transition-colors z-10"
            >
              <ArrowDown className="w-3.5 h-3.5 text-brand-rose" /> novas mensagens
            </button>
          )}

          {!voice.active && !introVisible && (
            <div className="shrink-0 border-t border-brand-line px-4 lg:px-7 py-2" data-testid="persona-voice-actions">
              <p role="status" className="text-[12px] text-brand-ink2 font-ui mb-2">
                {historyError ? "Não foi possível carregar sua conversa. Tente novamente para recuperar seu progresso."
                  : !historyReady ? "Carregando sua conversa..."
                  : voice.status === "error" ? "Não foi possível iniciar a voz. Tente novamente ou continue digitando."
                  : hasConversation ? "Seu progresso está salvo. Continue por voz ou texto."
                  : "Toque para iniciar a voz ou escreva sua resposta abaixo."}
              </p>
              <div className="flex flex-wrap gap-2">
                {historyError && (
                  <button type="button" data-testid="persona-retry-history"
                    onClick={() => { initRef.current = false; setLoadAttempt((n) => n + 1); }}
                    className="rounded-full border border-brand-line text-brand-ink px-4 py-2 text-[13px] font-ui min-h-[44px]">
                    Recarregar conversa
                  </button>
                )}
                <button type="button" data-testid="persona-start-voice" onClick={startVoice}
                  disabled={!historyReady || !thread?.id || sending}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-rose hover:bg-brand-roseHover text-white px-4 py-2 text-[13px] font-ui min-h-[44px] disabled:opacity-50">
                  <Mic className="w-4 h-4" />{voiceActionLabel}
                </button>
                <button type="button" data-testid="persona-use-text" disabled={!historyReady || !thread?.id}
                  onClick={() => { setShowIntro(false); inputRef.current?.focus(); }}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line text-brand-ink px-4 py-2 text-[13px] font-ui min-h-[44px] disabled:opacity-50">
                  <Keyboard className="w-4 h-4" />Digitar
                </button>
              </div>
            </div>
          )}

          {transitionError && (
            <div className="shrink-0 border-t border-brand-line px-4 lg:px-7 py-2" role="alert">
              <div className="flex flex-wrap items-center gap-2 text-[12px] text-brand-ink2 font-ui">
                <span>Não confirmamos a mudança de etapa. Sua conversa continua salva.</span>
                <button
                  type="button"
                  data-testid="persona-retry-avatar-transition"
                  onClick={() => transitionRetry === "celebration" ? finishCelebration() : advanceToAvatar()}
                  className="rounded-full border border-brand-line text-brand-ink px-3 py-1.5 hover:border-brand-rose min-h-[36px]"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          <ChatComposer
            inputRef={inputRef}
            value={input}
            onChange={setInput}
            onSend={() => sendMessage()}
            onStop={stopStreaming}
            sending={sending}
            queued={queued}
            placeholder="Conta pra ValiosIA..."
            onFiles={thread ? attach.attachFiles : undefined}
            attachItems={attach.items}
            threadDocs={docsFromMessages(messages)}
            onUseDoc={(doc) => sendMessage(promptToUseDoc(doc.filename))}
            onDetachDoc={detachDoc}
            attaching={attach.attaching}
            fileInputRef={attach.inputRef}
            onMicToggle={() => (voice.active ? endVoice() : startVoice())}
            micDisabled={sending}
            voiceActive={voice.active}
            voiceStatus={voice.status}
            disabled={!thread || !historyReady}
            testids={{ input: "persona-input", send: "persona-send", voice: "voice-toggle", attach: "persona-attach" }}
          />
          {voice.active && !voice.recovery && (
            <VoiceHelpAction onClick={voice.reportAudioProblem} />
          )}

          {voice.recovery && (
            <AudioFirstOverlay
              title={voice.recovery.title}
              description={voice.recovery.message}
              onVoice={voice.resume}
              onText={continueVoiceByText}
              voiceLabel={voice.recovery.actionLabel || "Retomar voz"}
              textLabel="Continuar por texto"
              recovery
            />
          )}

          {introVisible && (
            <AudioFirstOverlay
              title="Vamos criar sua marca conversando?"
              description="É uma conversa contínua, não uma gravação. A ValiosIA te entrevista ao vivo. Prefere digitar? Dá pra alternar a qualquer momento."
              onVoice={startVoice}
              onText={() => setShowIntro(false)}
            />
          )}
        </div>

        {/* Persona card — desktop own scroll column */}
        <div className="hidden lg:block min-h-0 overflow-y-auto overflow-x-hidden scrollbar-soft pb-2">
          <PersonaCard persona={persona} />
        </div>
      </div>

      {/* Mobile persona sheet */}
      <Sheet open={personaSheet} onOpenChange={setPersonaSheet}>
        <SheetContent side="bottom" data-testid="persona-sheet" className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden scrollbar-soft">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">Sua marca</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <PersonaCard persona={persona} />
          </div>
        </SheetContent>
      </Sheet>

      <OnboardingCelebration show={celebrate} onDone={finishCelebration} />
    </div>
  );
}
